import type { ValueGetterParams } from "ag-grid-community";
import { type FC, type Key, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { Item } from "react-stately";
import {
  ColoredBullet,
  HeaderDivider,
  MenuOptionItem,
  Select,
  SymbolIcon,
  type TagIntent,
  TriggerButton,
  VStack,
} from "widgets";

import { getTagIntentColor } from "@/app-utils/events.ts";
import { matchFilter, parseFilterExpression } from "@/app-utils/filter.ts";
import { useError } from "@/app-utils/graphql/useError.ts";
import { useDocumentTitle } from "@/app-utils/hooks/useDocumentTitle.ts";
import { pluralizeWord, toTitleCase } from "@/app-utils/string.ts";
import type { PlaybookEvent } from "@/graphql.ts";
import { JobAdminType, JobStatusEnum } from "@/graphql.ts";
import { matchStatusToTagIntent } from "@/modules/jobs/utils/statusToTag.ts";
import { SubscriptionTypeEnum } from "@/modules/notifications/utils/types.ts";
import { getUserDisplayName } from "@/modules/users/utils/user.ts";
import type { IAppState } from "@/redux/reducer.ts";
import { CELL_TYPE, EVENT_STATUS, SORT_ORDER_ASC } from "@/settings/settings.ts";
import { webSocketClient } from "@/tools.ts";
import {
  type JobAdminListingFragment,
  useGetJobsForAdminListingLazyQuery,
} from "@/ui-modules/admin/graphql/job-admin-queries.gql.ts";
import { getJobAdminTypeDisplayName } from "@/ui-modules/admin/utils/utils.ts";
import DataContextIndicator from "@/ui-modules/core/grid/DataContextIndicator.tsx";
import KillWorkflowButton from "@/ui-modules/core/KillWorkflowButton.js";

import type { IIdentifiable, ITableColumn } from "../../redux/modules/table-data.js";
import { AsyncSearchWithExprParser } from "../core/forms/AsyncSearchWithExprParser.js";
import InMemoryTableWithSelectionModel from "../core/grid/InMemoryTableWithSelectionModel.js";
import PageHeader from "../core/navigation-panel/PageHeader.js";
import styleLayout from "../core/styles/PageLayout.module.scss";

const jobsAdminColumnDefinition: Partial<ITableColumn>[] = [
  {
    name: "Title",
    field: "job.title",
    cellType: CELL_TYPE.STRING,
  },
  {
    name: "Executed",
    field: "job.created",
    cellType: CELL_TYPE.DATE_TIME,
  },
  {
    name: "Status",
    field: "job.status",
    cellType: CELL_TYPE.STRING,
    sort: { direction: SORT_ORDER_ASC.toLowerCase(), priority: 1 },
    comparator: (valueA: JobStatusEnum, valueB: JobStatusEnum) => {
      if (!valueB) return -1;
      if (!valueA) return 1;
      return jobStatusOrdering[valueA] - jobStatusOrdering[valueB];
    },
  },
  {
    name: "Type",
    field: "job.type",
    cellType: CELL_TYPE.STRING,
    valueGetter: (params: ValueGetterParams) =>
      getJobAdminTypeDisplayName((params.data.job as JobAdminListingFragment).type),
  },
  {
    name: "Owner",
    field: "job.owner",
    cellType: CELL_TYPE.STRING,
    valueGetter: (params: ValueGetterParams) => getUserDisplayName((params.data.job as JobAdminListingFragment).owner),
  },
  {
    name: "More Details",
    field: "job.details",
    cellType: CELL_TYPE.STRING,
  },
];

const jobStatusOrdering: Record<JobStatusEnum, number> = {
  [JobStatusEnum.Queued]: 1,
  [JobStatusEnum.Initializing]: 2,
  [JobStatusEnum.Running]: 3,
  [JobStatusEnum.Error]: 4,
  [JobStatusEnum.Terminated]: 5,
  [JobStatusEnum.Completed]: 6,
};

/**
 * Jobs Admin Page main component
 */
const JobsAdminPage: FC = () => {
  useDocumentTitle("Jobs - Administration");
  const lastNotification = useSelector<IAppState, PlaybookEvent | null>(
    (state) => state.notifications.lastNotification?.playbookEvent,
    shallowEqual,
  );
  const gridInstance = useRef<InMemoryTableWithSelectionModel>();

  const [jobs, setJobs] = useState<JobAdminListingFragment[]>([]);
  const [rows, setRows] = useState<IIdentifiable[]>([]);
  const [filterText, setFilterText] = useState<string>("");
  const [filterByType, setFilterByType] = useState<JobAdminType | "all" | undefined>("all");
  const [filterByStatus, setFilterByStatus] = useState<JobStatusEnum | "all" | undefined>("all");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const [fetchJobs, { error: fetchJobsError, loading: loadingJobs }] = useGetJobsForAdminListingLazyQuery();
  void useError(fetchJobsError);

  const updateJobsListing = useCallback(async () => {
    gridInstance?.current?.resetSelection();
    const { data } = await fetchJobs();
    const jobs: JobAdminListingFragment[] = data?.admin?.jobs ?? [];
    setJobs(jobs);
  }, [fetchJobs]);

  useEffect(() => {
    void updateJobsListing();
  }, [updateJobsListing]);

  const selectedPlaybookEvents = useMemo(
    () =>
      jobs
        .filter((j) => j.type === JobAdminType.PlaybookEvent)
        .filter((job) => selectedIds.includes(job.id))
        .map((j) => j.id),
    [selectedIds, jobs],
  );

  useEffect(() => {
    if (
      lastNotification?.id &&
      selectedPlaybookEvents.includes(lastNotification?.id) &&
      lastNotification?.status === EVENT_STATUS.TERMINATED
    ) {
      updateJobsListing();
    }
  }, [lastNotification, selectedPlaybookEvents, updateJobsListing]);

  useEffect(() => {
    const rows = jobs
      .filter((job: JobAdminListingFragment) =>
        matchFilter(
          [job.owner.firstName, job.owner.lastName, job.owner.username, job.status, job.title, job.details],
          parseFilterExpression(filterText),
        ),
      )
      .filter((job: JobAdminListingFragment) =>
        !filterByType || filterByType === "all" ? true : job.type === filterByType,
      )
      .filter((job: JobAdminListingFragment) =>
        !filterByStatus || filterByStatus === "all" ? true : job.status === filterByStatus,
      )
      // make sure the cell matching will target the right cell by field
      .map(
        (job: JobAdminListingFragment) =>
          ({
            id: job.id,
            job: job,
            isSelectable:
              job.killable &&
              (job.status == JobStatusEnum.Queued ||
                job.status == JobStatusEnum.Initializing ||
                job.status == JobStatusEnum.Running),
          }) satisfies IIdentifiable,
      );

    setRows(rows);
  }, [jobs, filterText, filterByType, filterByStatus]);

  const selectedWorkflowSessions = useMemo(
    () =>
      jobs
        .filter((j) => j.type === JobAdminType.WorkflowEvent)
        .filter((job) => selectedIds.includes(job.id))
        .map((j) => j.id),
    [selectedIds, jobs],
  );

  useEffect(() => {
    let subscriptionIds: string[] = [];

    if (selectedWorkflowSessions.length > 0) {
      selectedWorkflowSessions.forEach((sessionId) => {
        const subscriptionId = webSocketClient.subscribe({
          subscription: {
            eventType: SubscriptionTypeEnum.WorkflowActivity,
            variables: { workflowEventId: sessionId },
          },
          onMessage: (message) => {
            if (message.workflowEventId === sessionId) {
              if (message.workflow && message.workflow.status === JobStatusEnum.Terminated) {
                updateJobsListing();
                // unsubscribe right away since it has terminated
                webSocketClient.unsubscribe(subscriptionId);
                subscriptionIds = subscriptionIds.filter((sub) => sub !== subscriptionId);
              }
            }
          },
        });
        subscriptionIds.push(subscriptionId);
      });
    }

    return () => {
      // unsubscribe all subscriptions in case it failed before
      if (subscriptionIds.length > 0) {
        subscriptionIds.forEach((sessionId) => webSocketClient.unsubscribe(sessionId));
      }
    };
  }, [selectedWorkflowSessions, updateJobsListing]);

  return (
    <VStack spacing={0} className={styleLayout.page} style={{ width: "100%", height: "100%", overflow: "hidden" }}>
      <PageHeader
        title="Jobs"
        subtitle="In progress and recent jobs"
        badge={`${jobs.length}`}
        toolsWidgets={[
          <AsyncSearchWithExprParser
            onSearchExprChange={async (value) => setFilterText(value)}
            key="jobs-admin-page-widget-1"
          />,
          <JobsFilterByTypeWidget
            setFilterByType={setFilterByType}
            filterByType={filterByType}
            key="jobs-admin-page-widget-2"
          />,
          <JobsFilterByStatusWidget
            setFilterByStatus={setFilterByStatus}
            filterByStatus={filterByStatus}
            key="jobs-admin-page-widget-3"
          />,
        ]}
        actionWidgets={[
          <KillWorkflowButton
            key="kill-button"
            selectedIds={selectedIds}
            isDisabled={selectedIds.length == 0}
            tooltip={`Terminate the running ${pluralizeWord("Job", selectedIds.length)}`}
          />,
          <HeaderDivider key="widgets-separator" />,
          <TriggerButton
            key="refresh-button"
            tooltip="Refresh the list"
            onPress={updateJobsListing}
            isDisabled={loadingJobs}
            icon="sync"
            label="Refresh"
            showLabel
          />,
        ]}
      />

      <div style={{ flex: 1 }}>
        <InMemoryTableWithSelectionModel
          ref={gridInstance}
          isLoading={loadingJobs}
          columns={jobsAdminColumnDefinition}
          rowGridData={rows}
          useSelectionModel={true}
          uniqueGridIdentifier="jobs-admin"
          selectionModelUpdated={(model) => {
            setSelectedIds([...(model.selectedIds as number[])]);
          }}
        />
        <DataContextIndicator
          key="context-indicator"
          numTotalRows={jobs.length}
          numRowsFiltered={rows?.length}
          rowEntityLabel="job"
          filterIsSet={
            !!filterText?.length ||
            (!!filterByType && filterByType !== "all") ||
            (!!filterByStatus && filterByStatus !== "all")
          }
          numRowsSelected={selectedIds.length}
        />
      </div>
    </VStack>
  );
};

interface JobsFilterByTypeWidgetProps {
  filterByType: JobAdminType | "all" | undefined;
  setFilterByType: (key: JobAdminType) => void;
}

const JobsFilterByTypeWidget: FC<JobsFilterByTypeWidgetProps> = (props) => {
  return (
    <Select
      onSelectionChange={props.setFilterByType as (key: Key) => void}
      selectedKey={props.filterByType}
      width={110}
      icon="compare_arrows"
    >
      <Item key="all" textValue="All Event Types">
        <MenuOptionItem title="All Event Types" />
      </Item>
      {Object.values(JobAdminType).map((jobAdminType: JobAdminType) => {
        return (
          <Item key={jobAdminType} textValue={getJobAdminTypeDisplayName(jobAdminType)}>
            <MenuOptionItem title={getJobAdminTypeDisplayName(jobAdminType)} />
          </Item>
        );
      })}
    </Select>
  );
};

interface JobsFilterByStatusWidgetProps {
  filterByStatus: JobStatusEnum | "all" | undefined;
  setFilterByStatus: (key: JobStatusEnum) => void;
}

const JobsFilterByStatusWidget: FC<JobsFilterByStatusWidgetProps> = (props) => {
  return (
    <Select
      onSelectionChange={props.setFilterByStatus as (key: Key) => void}
      selectedKey={props.filterByStatus}
      width={110}
      icon="laps"
    >
      <Item key="all" textValue="All Statuses">
        <MenuOptionItem title="All Statuses" icon={<SymbolIcon icon="fiber_smart_record" filled />} />
      </Item>
      {Object.keys(jobStatusOrdering).map((jobStatus: JobStatusEnum) => {
        return (
          <Item key={jobStatus} textValue={toTitleCase(jobStatus)}>
            <MenuOptionItem
              title={toTitleCase(jobStatus)}
              icon={
                <ColoredBullet
                  color={getTagIntentColor(matchStatusToTagIntent(jobStatus).intentColor as TagIntent)}
                  style={{ width: 20, display: "flex", justifyContent: "center" }}
                />
              }
            />
          </Item>
        );
      })}
    </Select>
  );
};

export default JobsAdminPage;
